import React from 'react';
import { Link } from 'react-router-dom';
import {ArrowRight, Calculator, Ticket, Calendar, MapPin, Compass, BookOpen, Tag} from 'lucide-react';
import { motion } from 'framer-motion';
import { useScrollAnimation, useParallax } from '../hooks/useScrollAnimation';

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const scrollY = useParallax();

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section with Parallax */}
      <section className="relative bg-gradient-to-br from-[#0A2540] via-[#1A3A5A] to-[#0A2540] text-white py-20 md:py-32 overflow-hidden">
        {/* Parallax Background Elements */}
        <motion.div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        />
        
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-[#FF6B35] rounded-full opacity-10 blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        />
        
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#4A90E2] rounded-full opacity-10 blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.4}px)` }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Travel Smarter With Just Enough Information
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="text-xl md:text-2xl mb-4 text-gray-300"
          >
            Your Essential Guide to Theme Parks, Attractions & Travel Planning
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
            className="text-lg mb-8 text-gray-400 max-w-3xl mx-auto"
          >
            Cut through the noise. Get practical tips, insider insights, and smart tools to plan your perfect trip—without the overwhelm.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/destinations"
              className="bg-[#FF6B35] px-8 py-4 rounded-lg font-semibold hover:bg-[#E55A2B] transition-all transform hover:scale-105 shadow-lg"
            >
              Explore Destinations
            </Link>
            <Link
              to="/tools"
              className="bg-white text-[#0A2540] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
            >
              Try Our Tools
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <AnimatedSection>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-[#0A2540]">Featured Destinations</h2>
            <Link
              to="/destinations"
              className="text-[#FF6B35] font-semibold flex items-center gap-2 hover:gap-3 transition-all"
            >
              View All <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { name: 'Japan', description: 'Theme parks, culture & cuisine', color: 'from-red-500 to-pink-500' },
            { name: 'Singapore', description: 'Urban adventures & attractions', color: 'from-green-500 to-teal-500' },
            { name: 'Hong Kong', description: 'City sights & island escapes', color: 'from-blue-500 to-cyan-500' },
            { name: 'USA', description: 'Epic theme parks & experiences', color: 'from-indigo-500 to-purple-500' },
          ].map((dest, index) => (
            <AnimatedSection key={dest.name} delay={index * 0.1}>
              <Link to={`/destinations/${dest.name.toLowerCase()}`} className="group block">
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5 }}
                  transition={{ duration: 0.3 }}
                  className={`relative h-64 bg-gradient-to-br ${dest.color} rounded-xl overflow-hidden shadow-lg`}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{dest.name}</h3>
                    <p className="text-sm opacity-90">{dest.description}</p>
                  </div>
                </motion.div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Featured Tools */}
      <section className="bg-white py-16 relative overflow-hidden">
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-[#F7B32B] rounded-full opacity-5 blur-3xl"
          style={{ transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)` }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-[#0A2540]">Travel Tools</h2>
              <Link
                to="/tools"
                className="text-[#FF6B35] font-semibold flex items-center gap-2 hover:gap-3 transition-all"
              >
                All Tools <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Calculator, name: 'Budget Calculator', description: 'Plan your trip expenses', color: 'bg-[#4A90E2]' },
              { icon: Ticket, name: 'Ticket Finder', description: 'Compare attraction prices', color: 'bg-[#FF6B35]' },
              { icon: Calendar, name: 'Best Time to Visit', description: 'Find ideal travel dates', color: 'bg-[#F7B32B]' },
            ].map((tool, index) => (
              <AnimatedSection key={tool.name} delay={index * 0.15}>
                <Link to={`/tools/${tool.name.toLowerCase().replace(/ /g, '-')}`} className="group block">
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-all"
                  >
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className={`${tool.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}
                    >
                      <tool.icon className="w-6 h-6 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-[#0A2540] mb-2">{tool.name}</h3>
                    <p className="text-gray-600">{tool.description}</p>
                  </motion.div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Guides */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <AnimatedSection>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-[#0A2540]">Popular Guides</h2>
            <Link
              to="/guides"
              className="text-[#FF6B35] font-semibold flex items-center gap-2 hover:gap-3 transition-all"
            >
              All Guides <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: MapPin, title: 'First-Time Visitor Guides', category: 'Getting Started' },
            { icon: Compass, title: 'Sample Itineraries', category: 'Planning' },
            { icon: BookOpen, title: 'Transport & Getting Around', category: 'Logistics' },
          ].map((guide, index) => (
            <AnimatedSection key={guide.title} delay={index * 0.1}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.3 }}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer group"
              >
                <motion.div
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <guide.icon className="w-8 h-8 text-[#FF6B35] mb-4" />
                </motion.div>
                <p className="text-sm text-[#4A90E2] font-semibold mb-2">{guide.category}</p>
                <h3 className="text-xl font-semibold text-[#0A2540]">{guide.title}</h3>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Deals Preview with Parallax */}
      <section className="relative bg-gradient-to-r from-[#FF6B35] to-[#F7B32B] py-16 overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'linear-gradient(45deg, transparent 30%, white 30%, white 70%, transparent 70%)',
            backgroundSize: '100px 100px',
            transform: `translateX(${scrollY * 0.2}px)`,
          }}
        />

        <AnimatedSection>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white relative z-10">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Tag className="w-12 h-12 mx-auto mb-4" />
            </motion.div>
            <h2 className="text-3xl font-bold mb-4">Find the Best Deals</h2>
            <p className="text-xl mb-8 opacity-90">
              Compare ticket prices, passes, and bundles to save on your next adventure
            </p>
            <Link
              to="/deals"
              className="inline-block bg-white text-[#FF6B35] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
            >
              View All Deals
            </Link>
          </div>
        </AnimatedSection>
      </section>

      {/* About Snippet */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <AnimatedSection>
          <h2 className="text-3xl font-bold text-[#0A2540] mb-6">Why JE Inform?</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            We believe in giving you <strong>just enough information</strong> to make smart travel decisions—without the overwhelm.
            Our curated guides, practical tools, and honest comparisons help you plan incredible trips to the world's best theme parks
            and attractions. No fluff, no endless scrolling—just the essentials you need.
          </p>
          <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.3 }}>
            <Link
              to="/about"
              className="inline-block mt-6 text-[#FF6B35] font-semibold flex items-center gap-2 justify-center hover:gap-3 transition-all"
            >
              Learn More About Us <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </AnimatedSection>
      </section>
    </div>
  );
}
